import axiosInstance from '@/services/axiosInstance';
import { ExtendedUserResponseSchema, UserInviteRequestSchema, UserUpdateRequestSchema } from '@/types/user.types';
import { GenericErrorResponse, GenericErrorResponseError, UserInviteResponse } from '@brainvaulttech/dgr-react-sdk';
import { useMutation, useQuery } from '@tanstack/react-query';

export const useInviteUser = () => {
  return useMutation<UserInviteResponse, GenericErrorResponse, { data: UserInviteRequestSchema }>({
    mutationFn: async ({ data }) => {
      const response = await axiosInstance.post<UserInviteResponse>(`/users/invite-user`, {
        ...data,
      });
      return response.data;
    },
  });
};

export const useGetUserById = (userId: string | undefined) => {
  return useQuery<ExtendedUserResponseSchema, GenericErrorResponseError>({
    queryKey: ['user', userId],
    queryFn: async () => {
      if (!userId) throw new Error('userId is required');

      const response = await axiosInstance.get<ExtendedUserResponseSchema>(`/users/${userId}`);

      return response.data;
    },
    enabled: !!userId,
  });
};

export const useUpdateUser = () => {
  return useMutation<{ message: string }, GenericErrorResponse, { userId?: string; data: UserUpdateRequestSchema }>({
    mutationFn: async ({ userId, data }) => {
      if (!userId) throw new Error('userId is required');
      const response = await axiosInstance.put<{ message: string }>(`/users/${userId}`, {
        ...data,
      });
      return response.data;
    },
  });
};

export const isSupervisor = (user: ExtendedUserResponseSchema | null): boolean => {
  if (!user) return false;
  return user.teams?.some((team) => team.supervisorId === user.userId) ?? false;
};

export const isInTeam = (
  user: ExtendedUserResponseSchema | null,
  teamKey: string
): boolean => user?.teams?.some((team) => team.key === teamKey) ?? false;

export const isInAnyTeam = (
  user: ExtendedUserResponseSchema | null,
  teamKeys: string[]
): boolean => teamKeys.some((key) => isInTeam(user, key));

export const isBillingTeamMember = (user: ExtendedUserResponseSchema | null): boolean =>
  isInTeam(user, 'BILLING');

export const isAccountingTeamMember = (user: ExtendedUserResponseSchema | null): boolean =>
  isInTeam(user, 'ACCOUNTING');

export const isDueDiligenceTeamMember = (user: ExtendedUserResponseSchema | null): boolean =>
  isInTeam(user, 'DUE_DILIGENCE');

export const canAccessProcessBilling = (user: ExtendedUserResponseSchema | null): boolean => {
  if (!user) return false;
  if (user.roleId === 'ADMIN' || user.roleId === 'ACCOUNTANT') return true;
  if (isBillingTeamMember(user)) return true;
  if (isSupervisor(user)) return true;
  return false;
};

export const canAccessAccounting = (user: ExtendedUserResponseSchema | null): boolean => {
  if (!user) return false;
  if (user.roleId === 'ADMIN' || user.roleId === 'ACCOUNTANT') return true;
  if (isAccountingTeamMember(user)) return true;
  return false;
};

export const isBillingTeamSupervisor = (user: ExtendedUserResponseSchema | null): boolean => {
  if (!user) return false;
  return user.teams?.some((team) => team.key === 'BILLING' && team.supervisorId === user.userId) ?? false;
};

export const isAdminOrSupervisor = (user: ExtendedUserResponseSchema | null): boolean => {
  if (!user) return false;
  if (user.roleId === 'ADMIN') return true;
  return isSupervisor(user);
};

/** Mirrors API assign-other: only admin/supervisor may pick an assignee on create/edit forms. */
export const canSelectDdiAssigneeOnForm = (user: ExtendedUserResponseSchema | null): boolean =>
  isAdminOrSupervisor(user);

/** Mirrors API self-assign: DDI team member, supervisor, or admin. */
export const canAssignDdiWorkOrderToMe = (user: ExtendedUserResponseSchema | null): boolean =>
  isDueDiligenceTeamMember(user) || isAdminOrSupervisor(user);
